import { useEffect, useState } from "react";
import { motion, Reorder } from "framer-motion";
import { 
  Plus, Trash2, Edit, Save, X, GripVertical, 
  Palette, Check, RotateCcw, AlertCircle
} from "lucide-react";
import { Button } from "../ui/button";
import axios from "axios";
import { API, getAuthHeader } from "./utils";

/**
 * SportsTab - Admin component for managing sports sitewide
 * Sports created here are used in broadcasts, events, teams, etc.
 */
export const SportsTab = () => {
  const [sports, setSports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [reordering, setReordering] = useState(false);

  const [form, setForm] = useState({
    name: "",
    abbreviation: "",
    icon: "",
    color: "#FF5722",
    is_active: true,
    display_order: 0
  });

  // Common sport icons for quick selection
  const SPORT_ICONS = [
    { icon: "🏈", name: "Football" },
    { icon: "🏀", name: "Basketball" },
    { icon: "⚽", name: "Soccer" },
    { icon: "⚾", name: "Baseball" },
    { icon: "🥎", name: "Softball" },
    { icon: "🏐", name: "Volleyball" },
    { icon: "🤼", name: "Wrestling" },
    { icon: "🎾", name: "Tennis" },
    { icon: "⛳", name: "Golf" },
    { icon: "🏃", name: "Track" },
    { icon: "🏊", name: "Swimming" },
    { icon: "🥍", name: "Lacrosse" },
    { icon: "🏒", name: "Hockey" },
    { icon: "🏑", name: "Field Hockey" },
    { icon: "🎳", name: "Bowling" },
    { icon: "🏸", name: "Badminton" },
    { icon: "🥊", name: "Boxing" },
    { icon: "⛸️", name: "Skating" },
    { icon: "🎿", name: "Skiing" },
    { icon: "🏋️", name: "Weightlifting" }
  ];

  const fetchSports = async () => {
    try {
      const res = await axios.get(`${API}/sports?active_only=${!showInactive}`);
      setSports(res.data.sports || []);
    } catch (err) {
      console.error("Error fetching sports:", err);
      setError("Failed to load sports");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactive]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);

    try {
      if (editing) {
        await axios.put(`${API}/sports/${editing}`, form, { headers: getAuthHeader() });
      } else {
        await axios.post(`${API}/sports`, form, { headers: getAuthHeader() });
      }
      
      await fetchSports();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save sport");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (sport) => {
    setForm({
      name: sport.name,
      abbreviation: sport.abbreviation || "",
      icon: sport.icon || "",
      color: sport.color || "#FF5722",
      is_active: sport.is_active,
      display_order: sport.display_order || 0
    });
    setEditing(sport.id);
    setShowForm(true);
  };

  const handleDelete = async (sportId, sportName) => {
    if (!confirm(`Are you sure you want to deactivate "${sportName}"? It will no longer appear in dropdowns.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/sports/${sportId}`, { headers: getAuthHeader() });
      await fetchSports();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to deactivate sport");
    }
  };

  const handleReactivate = async (sportId) => {
    try {
      await axios.post(`${API}/sports/${sportId}/reactivate`, {}, { headers: getAuthHeader() });
      await fetchSports();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to reactivate sport");
    }
  };

  const handleReorder = async (newOrder) => {
    setSports(newOrder);
    setReordering(true);

    try {
      const orderIds = newOrder.map(s => s.id);
      await axios.post(`${API}/sports/reorder`, orderIds, { headers: getAuthHeader() });
    } catch (err) {
      console.error("Error reordering:", err);
      await fetchSports(); // Revert on error
    } finally {
      setReordering(false);
    }
  };

  const resetForm = () => {
    setForm({
      name: "",
      abbreviation: "",
      icon: "",
      color: "#FF5722",
      is_active: true,
      display_order: 0
    });
    setEditing(null);
    setShowForm(false);
    setError("");
  };

  const autoGenerateAbbreviation = (name) => {
    if (!name) return "";
    // Generate abbreviation from name
    const words = name.split(/\s+/);
    if (words.length === 1) {
      return name.slice(0, 3).toUpperCase();
    }
    return words.map(w => w[0]).join("").toUpperCase().slice(0, 4);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Sports Management</h2>
          <p className="text-sm text-zinc-500">
            Manage sports that appear sitewide in broadcasts, events, and teams
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-zinc-400">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded border-zinc-600"
            />
            Show Inactive
          </label>
          <Button
            onClick={() => setShowForm(true)}
            className="bg-orange-500 hover:bg-orange-600 text-white font-secondary text-xs px-4 py-2 h-auto rounded-none"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Sport
          </Button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 p-3 flex items-center gap-2 text-red-400">
          <AlertCircle className="w-4 h-4" />
          {error}
          <button onClick={() => setError("")} className="ml-auto text-red-500 hover:text-red-400">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Add/Edit Form */}
      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-zinc-900 border border-orange-500/30 p-6"
        >
          <h3 className="text-lg font-bold text-white mb-4">
            {editing ? "Edit Sport" : "Add New Sport"}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Sport Name */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
                  Sport Name *
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => {
                    setForm({ 
                      ...form, 
                      name: e.target.value,
                      abbreviation: form.abbreviation || autoGenerateAbbreviation(e.target.value)
                    });
                  }}
                  placeholder="e.g., Boys Basketball"
                  className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
                  required
                />
              </div>

              {/* Abbreviation */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
                  Abbreviation
                </label>
                <input
                  type="text"
                  value={form.abbreviation}
                  onChange={(e) => setForm({ ...form, abbreviation: e.target.value.toUpperCase() })}
                  placeholder="e.g., BBB"
                  maxLength={5}
                  className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm uppercase"
                />
              </div>
            </div>

            {/* Icon Selection */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
                Icon (Emoji)
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {SPORT_ICONS.map(({ icon, name }) => (
                  <button
                    key={icon}
                    type="button"
                    onClick={() => setForm({ ...form, icon })}
                    className={`w-10 h-10 flex items-center justify-center text-xl rounded border transition-colors ${
                      form.icon === icon 
                        ? 'border-orange-500 bg-orange-500/20' 
                        : 'border-white/10 bg-zinc-800 hover:border-white/30'
                    }`}
                    title={name}
                  >
                    {icon}
                  </button>
                ))}
              </div>
              <input
                type="text"
                value={form.icon}
                onChange={(e) => setForm({ ...form, icon: e.target.value })}
                placeholder="Or type a custom emoji"
                className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
              />
            </div>

            {/* Color */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
                Brand Color
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  className="w-12 h-10 border-0 bg-transparent cursor-pointer"
                />
                <input
                  type="text"
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  className="w-32 bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
                />
                <div 
                  className="flex-1 h-10 rounded flex items-center justify-center text-white font-bold"
                  style={{ backgroundColor: form.color }}
                >
                  {form.name || "Preview"}
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
              <Button
                type="button"
                onClick={resetForm}
                className="bg-zinc-800 text-white hover:bg-zinc-700 font-secondary text-xs px-4 py-2 h-auto rounded-none"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving || !form.name}
                className="bg-orange-500 hover:bg-orange-600 text-white font-secondary text-xs px-4 py-2 h-auto rounded-none disabled:opacity-50"
              >
                {saving ? "Saving..." : editing ? "Update Sport" : "Create Sport"}
              </Button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Sports List */}
      <div className="bg-zinc-900/50 border border-white/10">
        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
          <span className="text-sm text-zinc-400">
            {sports.length} sport{sports.length !== 1 ? 's' : ''} configured
          </span>
          <span className="text-xs text-zinc-500">
            Drag to reorder
          </span>
        </div>

        <Reorder.Group 
          axis="y" 
          values={sports} 
          onReorder={handleReorder}
          className="divide-y divide-white/5"
        >
          {sports.map((sport) => (
            <Reorder.Item
              key={sport.id}
              value={sport}
              className={`flex items-center gap-4 px-4 py-3 ${
                !sport.is_active ? 'opacity-50 bg-zinc-900/50' : 'bg-transparent'
              } hover:bg-white/5 transition-colors cursor-grab active:cursor-grabbing`}
            >
              {/* Drag Handle */}
              <GripVertical className="w-4 h-4 text-zinc-600" />

              {/* Icon */}
              <div 
                className="w-10 h-10 flex items-center justify-center text-2xl rounded"
                style={{ backgroundColor: `${sport.color}20` }}
              >
                {sport.icon || sport.name[0]}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-white font-medium">{sport.name}</span>
                  {sport.abbreviation && (
                    <span className="text-xs text-zinc-500 bg-zinc-800 px-2 py-0.5 rounded">
                      {sport.abbreviation}
                    </span>
                  )}
                  {!sport.is_active && (
                    <span className="text-xs text-red-500 bg-red-500/10 px-2 py-0.5 rounded">
                      Inactive
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <div 
                    className="w-4 h-4 rounded"
                    style={{ backgroundColor: sport.color }}
                  />
                  <span className="text-xs text-zinc-500">{sport.color}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                {!sport.is_active ? (
                  <Button
                    onClick={() => handleReactivate(sport.id)}
                    className="bg-green-500/20 text-green-400 hover:bg-green-500/30 p-2 h-auto rounded-none"
                    title="Reactivate"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                ) : (
                  <>
                    <Button
                      onClick={() => handleEdit(sport)}
                      className="bg-zinc-800 text-white hover:bg-zinc-700 p-2 h-auto rounded-none"
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => handleDelete(sport.id, sport.name)}
                      className="bg-red-500/20 text-red-400 hover:bg-red-500/30 p-2 h-auto rounded-none"
                      title="Deactivate"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </div>
            </Reorder.Item>
          ))}
        </Reorder.Group>

        {sports.length === 0 && (
          <div className="p-8 text-center text-zinc-500">
            No sports configured. Click &quot;Add Sport&quot; to create one.
          </div>
        )}
      </div>

      {/* Help Text */}
      <div className="bg-zinc-800/50 p-4 text-sm text-zinc-500">
        <p className="font-bold text-zinc-400 mb-2">How it works:</p>
        <ul className="list-disc list-inside space-y-1">
          <li>Sports created here appear in dropdown menus throughout the site</li>
          <li>Broadcasts, events, teams, and recordings all use this list</li>
          <li>Drag sports to change their order in dropdown menus</li>
          <li>Deactivated sports are hidden but can be reactivated later</li>
        </ul>
      </div>
    </div>
  );
};

export default SportsTab;
